"use client"

import { useState, type ReactNode } from "react"

import { RecoverDeliveryCrmHandoffDialog } from "@/components/deliveries/recover-delivery-crm-handoff-dialog"
import { ReconcileDeliveryShipmentDialog } from "@/components/deliveries/reconcile-delivery-shipment-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useDeliveryShipmentsQuery } from "@/hooks/queries/delivery-shipments"
import { usePermissions } from "@/hooks/use-permissions"
import { DELIVERIES_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { DELIVERY_CRM_MAX_ATTEMPTS, DELIVERY_CRM_UNKNOWN_ERROR_CODE } from "@/lib/deliveries/crm-handoff-constants"
import type { DeliveryShipmentListItem } from "@/server-actions/delivery-shipments"

export function DeliveryShipmentsPanel() {
  const { can } = usePermissions()
  const canView = can(DELIVERIES_PERMISSIONS.VIEW)
  const canReconcile = can(DELIVERIES_PERMISSIONS.RECONCILE)
  const shipmentsQuery = useDeliveryShipmentsQuery({
    limit: 10,
    enabled: canView,
  })
  const [reconciling, setReconciling] = useState<DeliveryShipmentListItem | null>(
    null,
  )
  const [recoveringCrm, setRecoveringCrm] = useState<
    DeliveryShipmentListItem | null
  >(null)
  const shipments = shipmentsQuery.data?.pages.flatMap(page => page.shipments)
    ?? []

  if (!canView) return null

  let content: ReactNode
  if (shipmentsQuery.error) {
    content = (
      <Alert variant="destructive">
        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
          <span>Delivery operations could not be loaded.</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void shipmentsQuery.refetch()}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    )
  } else if (shipmentsQuery.isLoading) {
    content = <div className="h-24 animate-pulse rounded bg-muted" />
  } else if (shipments.length === 0) {
    content = (
      <p className="text-sm text-muted-foreground">
        No delivery operations have been recorded yet.
      </p>
    )
  } else {
    content = (
      <>
        <ShipmentTable
          shipments={shipments}
          canReconcile={canReconcile}
          onReconcile={setReconciling}
          onRecoverCrm={setRecoveringCrm}
        />
        {shipmentsQuery.hasNextPage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={shipmentsQuery.isFetchingNextPage}
            onClick={() => void shipmentsQuery.fetchNextPage()}
          >
            {shipmentsQuery.isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        ) : null}
      </>
    )
  }

  return (
    <div className="space-y-3 border-t pt-4">
      <div>
        <h3 className="text-sm font-medium">Recent delivery operations</h3>
        <p className="text-xs text-muted-foreground">
          Waybill status, tracking, delivery quote, and CRM handoff state.
        </p>
      </div>

      {content}

      <ReconcileDeliveryShipmentDialog
        shipment={reconciling}
        onOpenChange={open => { if (!open) setReconciling(null) }}
      />
      <RecoverDeliveryCrmHandoffDialog
        shipment={recoveringCrm}
        onOpenChange={open => { if (!open) setRecoveringCrm(null) }}
      />
    </div>
  )
}

function ShipmentTable({
  shipments,
  canReconcile,
  onReconcile,
  onRecoverCrm,
}: {
  shipments: DeliveryShipmentListItem[]
  canReconcile: boolean
  onReconcile: (shipment: DeliveryShipmentListItem) => void
  onRecoverCrm: (shipment: DeliveryShipmentListItem) => void
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Order</TableHead>
          <TableHead>Recipient</TableHead>
          <TableHead className="min-w-80">Delivery</TableHead>
          <TableHead>Product payment</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>TTN</TableHead>
          <TableHead>CRM</TableHead>
          <TableHead>Created</TableHead>
          {canReconcile ? <TableHead className="text-right">Action</TableHead> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {shipments.map(shipment => (
          <TableRow key={shipment.id}>
            <TableCell>
              <div className="max-w-64 font-medium">{shipment.orderName}</div>
              <div className="text-xs text-muted-foreground">
                {shipment.integrationName} <span>·</span> {shipment.orderReference}
              </div>
            </TableCell>
            <TableCell>
              <div className="font-medium">{shipment.recipientName ?? "—"}</div>
              <div className="whitespace-nowrap text-xs text-muted-foreground">
                {shipment.recipientPhone ?? "No phone recorded"}
              </div>
            </TableCell>
            <TableCell>
              <div className="font-medium">{shipment.deliveryMethod ?? "—"}</div>
              <div className="max-w-72 text-xs text-muted-foreground">
                {shipment.deliveryAddress ?? "No destination recorded"}
              </div>
              <div className="text-xs text-muted-foreground">
                <span>Fee:</span> {formatMinorAmount(
                  shipment.carrierPriceMinor,
                  shipment.carrierPriceCurrency,
                )} <span>· Paid by</span> {shipment.deliveryFeePayer ?? "unknown"}
              </div>
            </TableCell>
            <TableCell>
              <div className="font-medium">{shipment.merchandisePaymentMethod}</div>
              <div className="whitespace-nowrap text-xs text-muted-foreground">
                <span>Merchandise:</span> {formatMinorAmount(
                  shipment.merchandiseTotalMinor,
                  shipment.merchandiseTotalCurrency,
                )}
              </div>
            </TableCell>
            <TableCell>
              <ShipmentStatusBadge status={shipment.operationStatus} />
              <ShipmentStatusDetails shipment={shipment} />
            </TableCell>
            <TableCell>{shipment.trackingNumber ?? "—"}</TableCell>
            <TableCell><CrmHandoffStatus shipment={shipment} /></TableCell>
            <TableCell className="whitespace-nowrap">
              {new Date(shipment.createdAt).toLocaleString()}
            </TableCell>
            {canReconcile ? (
              <TableCell className="text-right">
                <ShipmentRecoveryAction
                  shipment={shipment}
                  onReconcile={onReconcile}
                  onRecoverCrm={onRecoverCrm}
                />
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function ShipmentStatusBadge({
  status,
}: {
  status: DeliveryShipmentListItem["operationStatus"]
}) {
  switch (status) {
    case "creating":
      return <Badge variant="secondary">Creating</Badge>
    case "created":
      return <Badge variant="default">Created</Badge>
    case "unknown":
      return <Badge variant="secondary">Needs verification</Badge>
    case "failed":
      return <Badge variant="destructive">Failed</Badge>
    case "cancelled":
      return <Badge variant="secondary">Cancelled</Badge>
    default: {
      const exhaustiveStatus: never = status
      return exhaustiveStatus
    }
  }
}

function ShipmentStatusDetails({
  shipment,
}: {
  shipment: DeliveryShipmentListItem
}) {
  switch (shipment.operationStatus) {
    case "unknown":
      return (
        <p className="mt-1 max-w-64 text-xs text-muted-foreground">
          Nova Post may have created this waybill. Check Nova Post before retrying.
        </p>
      )
    case "failed":
      return shipment.operationErrorCode ? (
        <div className="mt-1 max-w-48 break-words text-xs text-destructive">
          {shipment.operationErrorCode}
        </div>
      ) : null
    case "creating":
    case "created":
    case "cancelled":
      return null
    default: {
      const exhaustiveStatus: never = shipment.operationStatus
      return exhaustiveStatus
    }
  }
}

function CrmHandoffStatus({
  shipment,
}: {
  shipment: DeliveryShipmentListItem
}) {
  switch (shipment.crmSyncStatus) {
    case "not_required":
      return <Badge variant="outline">Not required</Badge>
    case "pending":
      return (
        <div className="space-y-1">
          <Badge variant="outline">Pending</Badge>
          <p className="max-w-56 text-xs text-muted-foreground">
            {shipment.operationStatus === "created"
              ? "The waybill exists; the worker notification is waiting to be sent."
              : "The manual-attention notification is waiting to be sent."}
          </p>
        </div>
      )
    case "synced":
      return <Badge variant="outline">{shipment.operationStatus === "created" ? "Worker notified" : "Manual attention flagged"}</Badge>
    case "failed":
      return <FailedCrmHandoffStatus shipment={shipment} />
    default: {
      const exhaustiveStatus: never = shipment.crmSyncStatus
      return exhaustiveStatus
    }
  }
}

function FailedCrmHandoffStatus({
  shipment,
}: {
  shipment: DeliveryShipmentListItem
}) {
  let message = shipment.operationStatus === "created"
    ? "The waybill exists, but the worker notification failed."
    : "The manual-attention notification failed."
  if (shipment.crmLastErrorCode === DELIVERY_CRM_UNKNOWN_ERROR_CODE) {
    message = "Check the CRM: the worker note may already exist."
  } else if (shipment.crmSyncAttempts >= DELIVERY_CRM_MAX_ATTEMPTS) {
    message = "Automatic retries are exhausted; operator action is required."
  } else if (shipment.crmNextAttemptAt) {
    message = `Retry scheduled for ${new Date(
      shipment.crmNextAttemptAt,
    ).toLocaleString()}.`
  }
  return (
    <div className="space-y-1">
      <Badge variant="destructive">Worker not notified</Badge>
      <p className="max-w-56 text-xs text-destructive">{message}</p>
    </div>
  )
}

function ShipmentRecoveryAction({
  shipment,
  onReconcile,
  onRecoverCrm,
}: {
  shipment: DeliveryShipmentListItem
  onReconcile: (shipment: DeliveryShipmentListItem) => void
  onRecoverCrm: (shipment: DeliveryShipmentListItem) => void
}) {
  if (shipment.operationStatus === "unknown") {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onReconcile(shipment)}
        >
          Verify in Nova Post
        </Button>
        {shipment.crmSyncStatus === "failed" ? (
          <Button type="button" size="sm" variant="outline" onClick={() => onRecoverCrm(shipment)}>
            Recover CRM handoff
          </Button>
        ) : null}
      </div>
    )
  }
  if (
    ["created", "failed", "unknown"].includes(shipment.operationStatus)
    && shipment.crmSyncStatus === "failed"
  ) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onRecoverCrm(shipment)}
      >
        Recover CRM handoff
      </Button>
    )
  }
  return null
}

function formatMinorAmount(amount: string | null, currency: string | null): string {
  if (!amount || !currency) return "—"
  const negative = amount.startsWith("-")
  const digits = negative ? amount.slice(1) : amount
  const padded = digits.padStart(3, "0")
  const major = `${padded.slice(0, -2)}.${padded.slice(-2)}`
  return `${negative ? "-" : ""}${major} ${currency}`
}
